const API_URL = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  const token = localStorage.getItem('ivypro_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...options.headers },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed: ${res.status}`);
  }
  return res.json();
}

export const api = {
  // Colleges
  getColleges: () => request('/api/colleges'),
  createCollege: (data) => request('/api/colleges', { method: 'POST', body: JSON.stringify(data) }),
  updateCollege: (id, data) => request(`/api/colleges/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCollege: (id) => request(`/api/colleges/${id}`, { method: 'DELETE' }),
  getMatchScore: (id) => request(`/api/colleges/${id}/match-score`, { method: 'POST' }),
  getAllMatchScores: () => request('/api/colleges/match-scores'),

  // School Research
  researchSchool: (query) => request('/api/school-research', { method: 'POST', body: JSON.stringify({ query }) }),
  suggestSchools: (q) => request(`/api/school-research/suggest?q=${encodeURIComponent(q)}`),

  // Essays
  getEssays: () => request('/api/essays'),
  getEssay: (id) => request(`/api/essays/${id}`),
  createEssay: (data) => request('/api/essays', { method: 'POST', body: JSON.stringify(data) }),
  updateEssay: (id, data) => request(`/api/essays/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEssay: (id) => request(`/api/essays/${id}`, { method: 'DELETE' }),

  // Activities
  getActivities: () => request('/api/activities'),
  createActivity: (data) => request('/api/activities', { method: 'POST', body: JSON.stringify(data) }),
  updateActivity: (id, data) => request(`/api/activities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteActivity: (id) => request(`/api/activities/${id}`, { method: 'DELETE' }),

  optimizeActivity: (data) => request('/api/activities/optimize', { method: 'POST', body: JSON.stringify(data) }),

  // Deadlines
  getDeadlines: () => request('/api/deadlines'),

  // Documents (FormData for file upload)
  getDocuments: () => request('/api/documents'),
  createDocument: (data, file) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('document_type', data.document_type || 'other');
    formData.append('notes', data.notes || '');
    if (file) formData.append('file', file);
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/documents`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    }).then(res => {
      if (!res.ok) return res.json().then(d => { throw new Error(d.detail || 'Upload failed'); });
      return res.json();
    });
  },
  updateDocument: (id, data, file) => {
    const formData = new FormData();
    if (data.name != null) formData.append('name', data.name);
    if (data.document_type != null) formData.append('document_type', data.document_type);
    if (data.notes != null) formData.append('notes', data.notes);
    if (file) formData.append('file', file);
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/documents/${id}`, {
      method: 'PUT',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    }).then(res => {
      if (!res.ok) return res.json().then(d => { throw new Error(d.detail || 'Update failed'); });
      return res.json();
    });
  },
  deleteDocument: (id) => request(`/api/documents/${id}`, { method: 'DELETE' }),
  extractDocumentData: (id) => request(`/api/documents/${id}/extract`, { method: 'POST' }),
  downloadDocument: (id) => {
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!res.ok) throw new Error('Download failed');
      return res.blob();
    });
  },

  // Activities batch
  batchOptimizeActivities: (activities) => request('/api/activities/batch-optimize', { method: 'POST', body: JSON.stringify({ activities }) }),
  reorderActivities: (order) => request('/api/activities/reorder', { method: 'PUT', body: JSON.stringify({ order }) }),

  // Recommenders
  getRecommenders: () => request('/api/recommenders'),
  createRecommender: (data) => request('/api/recommenders', { method: 'POST', body: JSON.stringify(data) }),
  updateRecommender: (id, data) => request(`/api/recommenders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRecommender: (id) => request(`/api/recommenders/${id}`, { method: 'DELETE' }),

  // Timeline
  getTimeline: () => request('/api/timeline'),
  createTimelineEvent: (data) => request('/api/timeline', { method: 'POST', body: JSON.stringify(data) }),
  updateTimelineEvent: (id, data) => request(`/api/timeline/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTimelineEvent: (id) => request(`/api/timeline/${id}`, { method: 'DELETE' }),

  // AI Feedback
  getAIFeedback: (essayId) => request(`/api/essays/${essayId}/ai-feedback`),
  generateAIFeedback: (essayId, data) => request(`/api/essays/${essayId}/ai-feedback`, { method: 'POST', body: JSON.stringify(data) }),
  getEssayPrompts: () => request('/api/essays/prompts'),

  // Resume
  generateResume: (data) => request('/api/resume/generate', { method: 'POST', body: JSON.stringify(data || {}) }),
  downloadResumePdf: () => {
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/resume/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error('PDF failed'); return res.blob(); });
  },
  downloadEssayPdf: (essayId) => {
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/essays/${essayId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error('PDF failed'); return res.blob(); });
  },

  // Interview Prep
  getInterviewQuestions: () => request('/api/interview/questions'),
  getInterviewFeedback: (data) => request('/api/interview/feedback', { method: 'POST', body: JSON.stringify(data) }),
  getInterviewHistory: () => request('/api/interview/history'),
  createInterviewSession: (data) => request('/api/interview/sessions', { method: 'POST', body: JSON.stringify(data) }),
  getInterviewSessions: () => request('/api/interview/sessions'),

  // Checklist
  getChecklist: () => request('/api/checklist'),
  downloadChecklistPdf: () => {
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/checklist/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error('PDF failed'); return res.blob(); });
  },

  // Dashboard
  getDashboardStats: () => request('/api/dashboard/stats'),

  // Schools
  getSchools: (params) => request(`/api/schools${params ? '?' + new URLSearchParams(params) : ''}`),
  getSchoolFilters: () => request('/api/schools/filters'),
  getSchoolDetail: (name) => request(`/api/schools/detail?name=${encodeURIComponent(name)}`),
  getMySchoolList: () => request('/api/colleges'),

  // Profile
  getProfile: () => request('/api/profile'),
  updateProfile: (data) => request('/api/profile', { method: 'PUT', body: JSON.stringify(data) }),

  // Packages
  getPackages: () => request('/api/packages'),

  // Leads
  captureLead: (data) => request('/api/leads/capture', { method: 'POST', body: JSON.stringify(data) }),

  // Analytics
  getAnalyticsSummary: (days = 30) => request(`/api/admin/analytics/summary?days=${days}`),

  // Admin
  getAdminStats: () => request('/api/admin/stats'),
  getAdminUsers: () => request('/api/admin/users'),
  getAdminUserDetail: (userId) => request(`/api/admin/users/${userId}`),
  grantUserAccess: (email, revoke = false) => request('/api/admin/grant-access', {
    method: 'POST',
    body: JSON.stringify({ email, revoke }),
  }),
  seedDemoAccounts: () => request('/api/admin/seed-demos', { method: 'POST' }),
  getDiagnosticSubmissions: () => request('/api/admin/diagnostic-submissions'),
  exportAdminUsers: () => {
    const token = localStorage.getItem('ivypro_token');
    return fetch(`${API_URL}/api/admin/export/users`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob());
  },
};
